class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.sondosRenderGeneration !== FacetFiltersForm.renderGeneration) return;
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (!FacetFiltersForm.confirmedControls) FacetFiltersForm.confirmControls();
    facetForm.addEventListener('input', (event) => {
      document.dispatchEvent(new CustomEvent('sondos:facets-start'));
      event.sondosRenderGeneration = FacetFiltersForm.renderGeneration;
      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-facet-retry]')) return;
      event.preventDefault();
      if (FacetFiltersForm.retryParams !== null) FacetFiltersForm.renderPage(FacetFiltersForm.retryParams);
    });
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    document.dispatchEvent(new CustomEvent('sondos:facets-start'));
    FacetFiltersForm.cancelRender(updateURLHash);
    const focused = document.activeElement;
    FacetFiltersForm.renderFocus = {
      id: focused.id,
      inDrawer: !!focused.closest('.mobile-facets__inner')
    };
    document.querySelectorAll('[data-facet-error]').forEach((el) => { el.hidden = true; });
    FacetFiltersForm.retryParams = null;
    /* Snapshot for abandonRender() (Sondos, 2026-09-13). updateURLHash below
       runs optimistically — before any response exists — so a failed fetch
       used to leave the address bar advertising a sort that never rendered.
       Captured before searchParamsPrev is overwritten, so the cache key for
       the state actually on screen survives too. */
    FacetFiltersForm.renderUndo = {
      href: window.location.href,
      prev: FacetFiltersForm.searchParamsPrev,
      hashed: updateURLHash,
      attempted: searchParams,
      controls: FacetFiltersForm.confirmedControls
    };
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const generation = FacetFiltersForm.renderGeneration;
    const controller = new AbortController();
    FacetFiltersForm.renderController = controller;
    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (generation !== FacetFiltersForm.renderGeneration) return;
        FacetFiltersForm.renderController = null;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderFilters(html, event, parsedHTML);
        FacetFiltersForm.renderProductGridContainer(html, parsedHTML);
        FacetFiltersForm.renderProductCount(html, parsedHTML);
        FacetFiltersForm.confirmControls();
        FacetFiltersForm.restoreFocus();
        FacetFiltersForm.renderUndo = null;
      })
      .catch(() => {
        if (generation === FacetFiltersForm.renderGeneration) {
          FacetFiltersForm.renderController = null;
          FacetFiltersForm.abandonRender(true, true);
        }
      });
  }

  static cancelRender(restoreURL = true) {
    FacetFiltersForm.renderGeneration++;
    if (FacetFiltersForm.renderController) {
      FacetFiltersForm.renderController.abort();
      FacetFiltersForm.renderController = null;
      FacetFiltersForm.abandonRender(restoreURL);
    }
  }

  /* Sondos, 2026-09-13 — independent audit finding.
     Dawn ships the fetch above with no `.catch` at all. renderPage() puts
     `.loading` on `.collection` and on both product counts, and the ONLY
     thing that takes them off again is a successful render: the grid class
     goes away because renderProductGridContainer replaces the whole
     container, the counts because renderProductCount removes it explicitly.
     So one dropped request left her looking at her own products under a
     spinner overlay that never stopped — no error, no retry, and the URL
     already rewritten to the option that failed. Measured with an injected
     connection failure: `.collection.loading` still set and overlay opacity
     still 1 after 11 s, both counts still loading, one unhandled rejection.

     This puts the page back the way it was rendered. It does not show an
     error and does not retry: her next tap on a filter or sort is the retry,
     and now it starts from an honest state instead of a stuck one.

     Note this file is shared with the search template, which runs the same
     grid and the same facets — so the same stuck overlay existed there. */
  static abandonRender(restoreURL = true, failed = false) {
    const container = document.getElementById('ProductGridContainer');
    const collection = container && container.querySelector('.collection');
    if (collection) collection.classList.remove('loading');

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.classList.remove('loading');
    });

    /* renderActiveFacets normally re-enables these on success. */
    FacetFiltersForm.toggleActiveFacets(false);

    const undo = FacetFiltersForm.renderUndo;
    if (!undo) return;
    FacetFiltersForm.renderUndo = null;
    /* Let the next attempt at the same params actually fetch again. */
    FacetFiltersForm.searchParamsPrev = undo.prev;
    if (failed) {
      FacetFiltersForm.restoreControls(undo.controls);
      FacetFiltersForm.retryParams = undo.attempted;
      const openDrawer = document.querySelector('.mobile-facets__disclosure[open]');
      const error = openDrawer ? openDrawer.querySelector('[data-facet-error]') : document.querySelector('[data-facet-error]');
      if (error) error.hidden = false;
    }
    /* replaceState, not back(): updateURLHash pushed an entry for a state
       that was never rendered, so the fix is to overwrite that entry, not to
       add another one — otherwise Back would walk her through a phantom. */
    if (restoreURL && undo.hashed && window.location.href !== undo.href) {
      try {
        history.replaceState({ searchParams: undo.prev }, '', undo.href);
      } catch (e) {
        /* cross-origin or history quota — the visible state is already correct */
      }
    }
  }

  static confirmControls() {
    FacetFiltersForm.confirmedControls = Array.from(document.querySelectorAll('facet-filters-form input:not([type="hidden"]), facet-filters-form select')).map((control) => ({
      id: control.id, form: control.closest('form')?.id, name: control.name, type: control.type,
      value: control.value, checked: control.checked
    }));
  }

  static restoreFocus() {
    const previous = FacetFiltersForm.renderFocus;
    FacetFiltersForm.renderFocus = null;
    if (!previous?.inDrawer) return;
    const generation = FacetFiltersForm.renderGeneration;
    // Visibility/focus reads flush layout after the grid and filters change.
    // Let that update paint first; a new render or a closed sheet owns focus.
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (generation !== FacetFiltersForm.renderGeneration) return;
        const drawer = document.querySelector('.mobile-facets__disclosure[open]');
        const panel = drawer?.querySelector('.mobile-facets__inner');
        if (!panel) return;
        const active = document.activeElement;
        if (panel.contains(active) && active.getClientRects().length) return;
        const target = previous.id && document.getElementById(previous.id);
        trapFocus(panel, target && panel.contains(target) ? target : panel.querySelector('input[name="sort_by"]:checked') || panel.querySelector('.mobile-facets__close-x'));
      }, 0);
    });
  }

  static restoreControls(controls) {
    document.querySelectorAll('facet-filters-form [name]').forEach((control) => {
      const saved = (controls || []).find((row) => row.id === control.id && row.form === control.closest('form')?.id && row.name === control.name && row.type === control.type &&
        (!['checkbox', 'radio'].includes(control.type) || row.value === control.value));
      if (!saved) return;
      if (['checkbox', 'radio'].includes(control.type)) control.checked = saved.checked;
      else control.value = saved.value;
    });
    document.querySelectorAll('price-range').forEach((range) => range.setMinAndMaxValues());
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(html, event, parsedHTML);
    FacetFiltersForm.renderProductGridContainer(html, parsedHTML);
    FacetFiltersForm.renderProductCount(html, parsedHTML);
    FacetFiltersForm.confirmControls();
    FacetFiltersForm.restoreFocus();
    FacetFiltersForm.renderUndo = null;
  }

  static renderProductGridContainer(html, parsedHTML) {
    const doc = parsedHTML || new DOMParser().parseFromString(html, 'text/html');
    // Reuse the parsed nodes instead of serialising and parsing 24 cards again.
    document.getElementById('ProductGridContainer').replaceChildren(...doc.getElementById('ProductGridContainer').childNodes);
  }

  static renderProductCount(html, parsedHTML) {
    const doc = parsedHTML || new DOMParser().parseFromString(html, 'text/html');
    const count = doc.getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event, documentHTML) {
    const parsedHTML = documentHTML || new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    // Empty fields stay out of the URL: the two price inputs are always in the
    // form, so every filter/sort used to publish
    // `?filter.v.price.gte=&filter.v.price.lte=&…` (QA, 2026-10-22). Shopify
    // treats an absent and an empty bound the same, so the results are
    // identical; the address bar, Back entries and shared links are cleaner.
    const params = new URLSearchParams();
    formData.forEach((value, key) => { if (value !== '') params.append(key, value); });
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.renderUndo = null;   /* set by renderPage, consumed by abandonRender */
FacetFiltersForm.renderGeneration = 0;
FacetFiltersForm.renderController = null;
FacetFiltersForm.retryParams = null;
FacetFiltersForm.confirmedControls = null;
FacetFiltersForm.renderFocus = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
